<template>
  <transition name="modal">
    <div class="modal-mask">
      <div class="modal-wrapper">
        <div class="modal-container">
          <div class="modal-close grow" @click="$store.commit('hideTurnInReview')">
            <i class="material-icons">close</i>
          </div>
          <div class="modal-title">Turn In Assignment</div>
          <div class="form-content">
            <div class="input-label">Your Rating</div>
            <div class="input-box">
              <v-radio-group v-model="rating" row @change="updateFields">
                <v-radio
                  class="star-radio"
                  v-for="n in 5"
                  :key="n"
                  :label="`${n} ${n === 1 ? 'star' : 'stars'}`"
                  :value="n"
                ></v-radio>
              </v-radio-group>
              <div
                class="field-instructions"
              >Please provide the number of stars you rated this book in your Amazon review.</div>
              <div class="field-error" v-if="ratingError">{{ ratingError }}</div>
            </div>
            <div class="clearfix"></div>

            <div class="input-label">Your Amazon Reviewer Name</div>
            <div class="input-box">
              <v-text-field
                v-model="amazonUsername"
                label
                required
                :disabled="amazonUsernameLocked"
                class="rating-field"
                @blur="updateFields"
              ></v-text-field>
              <div class="field-instructions">
                <span v-if="amazonUsernameLocked">
                  Your review must have been submitted under this Amazon Reviewer Name that you previously provided.
                  If it has changed, please contact us.
                </span>
                <span v-else>
                  Please provide the Amazon Reviewer Name that you posted the review with.
                  <br />All of your reviews must be made under the same Reviewer Name.
                  <br />
                  <br />To find your Reviewer Name, visit
                  <a
                    href="https://www.amazon.com/gp/profile"
                    rel="noopener noreferrer nofollow"
                  >https://www.amazon.com/gp/profile</a> while signed in to Amazon to see your Reviewer Name on the page in large text.
                </span>
              </div>
              <div class="field-error" v-if="amazonUsernameError">{{ amazonUsernameError }}</div>
            </div>
            <div class="clearfix"></div>

            <div class="input-label">Country Where You Posted Review</div>
            <div class="input-box">
              <v-select
                  v-model="countryCode"
                  required
                  :items="allowedCountries"
                  item-text="country_name"
                  item-value="country_code"
                  return-object
                  :disabled="countryCodeLocked"
                  class="rating-field"
                  @blur="updateFields"
                ></v-select>
                <div class="field-instructions">
                  <span v-if="countryCodeLocked">
                    Your review must have been submitted on the same country's Amazon page.
                    If you posted your review on a different country's page, please contact us.
                  </span>
                  <span v-else>
                    Please select the country's Amazon page where you have posted your review.
                    <span v-if="countryCode"><br />Your review will need to be posted on <a :href="'https://www.' + countryCode.amazon_domain" target="_blank">www.{{ countryCode.amazon_domain }}</a> for you to get credit!</span>
                  </span>
                </div>
                <div class="field-error" v-if="countryCodeError">{{ countryCodeError }}</div>
            </div>
            <div class="clearfix"></div>
            <div class="centered">
              <div class="field-error" v-if="turnaroundError">{{ turnaroundError }}</div>
            </div>

            <div class="actions">
              <div class="submit-review action" @click="submitReview">Submit Review</div>
            </div>
            <div class="clearfix"></div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Back from "../../modules/Back";
import Bus from "../../modules/Bus";
import Card from "../Assignment/Card";
import { mapState } from "vuex";

export default {
  name: "TurnInReview",
  props: ["assignment"],
  components: { Card },
  data: function() {
    return {
      // allowedCountries: null,
      rating: null,
      amazonUsername: null,
      countryCode: null,
      ratingError: null,
      amazonUsernameError: null,
      amazonUsernameLocked: false,
      countryCodeLocked: false,
      countryCodeError: false,
      hasNotTriedToSubmit: true,
      turnaroundError: null,
    };
  },
  computed: {
    ...mapState({
      user: "user",
      allowedCountries: "allowedCountries"
    }),
    offsetHeight() {
      // console.log("offsetHeight", window.innerHeight);
      return window.innerHeight - 300;
    },
    minTurnaroundTime() {
      const words = this.assignment.book.word_count //(this.assignment.points - 1000) / 0.005

      const hrsToRead = Math.ceil(words / 600) / 60

      if (hrsToRead < 0.5) {
        return 30 * 60 * 1000
      } else {
        return hrsToRead * 60 * 60 * 1000
      }

    }
  },
  created() {
    // console.log("stored user: ",this.$store.state.user);
    // console.log("stored countries: ",this.$store.state.allowedCountries);
    // this.allowedCountries = this.$store.state.allowedCountries;
    this.amazonUsername = this.user.amazon_username;

    if (this.amazonUsername) {
      this.amazonUsernameLocked = true;
    }

    this.countryCode = this.user.country_code;

    if (this.countryCode) {
      this.countryCodeLocked = true;
    } 
  
  },
  methods: {
    updateFields() {
      if (this.hasNotTriedToSubmit) {
        return
      }
      if (!this.rating) {
        this.ratingError = "The rating of your review is required";
      } else {
        this.ratingError = null;
      }

      if (!this.amazonUsername) {
        this.amazonUsernameError = "Your Amazon username is required";
      } else if(this.amazonUsernameInvalidMsg()) {
        this.amazonUsernameError = this.amazonUsernameInvalidMsg();
      } else {
        this.amazonUsernameError = null;
      }

      if (!this.countryCode) {
        this.countryCodeError = "Your country from which you are posting the review is required"
      } else {
        this.countryCodeError = null;
      }
    },
    amazonUsernameInvalidMsg() {
      //data validation for first-time user validation
      if (this.amazonUsernameLocked) {
        return
      }
      var errorMsg='';
      if (this.amazonUsername.includes('@')) {
        errorMsg= "It looks like you've entered your email as your Amazon Reviewer Name. Your Reviewer Name is never an email address. Please enter the name under which the review appears on Amazon."
      }
      const invalidNames = ["Reviewer", "Amazon Customer"];
      invalidNames.forEach(invalidName => {
        if (this.amazonUsername.toLowerCase()==invalidName.toLowerCase()) {
          errorMsg= `The entered Reviewer Name is too generic. Please change your Reviewer Name on Amazon to something more unique and enter that here.`;
        }
      });
      const namesInUse = ["Randfilms",'"Randfilms"',"'Sean Dollwet'","Sean Dollwet","SeanDollwet","'SeanDollwet'","Aparna",'"Aparna"'];
      namesInUse.forEach(nameInUse => {
        if (this.amazonUsername.toLowerCase()==nameInUse.toLowerCase()) {
          errorMsg= `The entered Reviewer Name is already in use on Pubby.`;
        }
      });
      return errorMsg;
    },
    checkTurnaroundTime(){
      //Verify the user has waited the defined amount of time before they can submit their review
      const now = new Date()
      const assignmentDate = new Date(
        this.assignment.started_at.replace(/-/g, "/")
        );
        const startTime = assignmentDate.getTime() - assignmentDate.getTimezoneOffset() * 60 * 1000;

      const earliestTurninDate = new Date(Math.round(
        (startTime + this.minTurnaroundTime)/(60*1000))*60*1000
      );
      const options = {hour: "numeric",minute: "numeric"}
      // console.log("turn in: ",earliestTurninDate)
      if (earliestTurninDate <= now) {
        console.log("timecheck passed")
        this.turnaroundError = null;
        return;
      }

      let waitTimeString = ""
      let hours = Math.floor(this.minTurnaroundTime / (1000 * 60 * 60));
      let minutes = Math.floor((this.minTurnaroundTime % (1000 * 60 * 60))/(1000 * 60));

      if (hours && minutes > 0 && minutes < 10) {
        waitTimeString = hours + ":0" + minutes + " hours";
      } else if (hours && minutes > 0 && minutes >= 10) {
        waitTimeString = hours + ":" + minutes + " hours";
      } else if (hours===1) {
        waitTimeString = hours + " hour"
      } else if (hours) {
        waitTimeString = hours + " hours"
      } else waitTimeString = minutes + " minutes"

      this.turnaroundError = "That was TOO quick! Based on this book's length, it would take a strong reader at least " + waitTimeString + " to read. Please wait until " + Intl.DateTimeFormat(undefined,options).format(earliestTurninDate) + " to turn in your review!"
    },
    async submitReview() {
      this.hasNotTriedToSubmit=false;
      this.updateFields();
      this.checkTurnaroundTime();

      if (this.ratingError || this.amazonUsernameError || this.countryCodeError || this.turnaroundError) {
        return;
      }

      // console.log("prop assignment", this.assignment);
      // console.log("country saved as", this.countryCode);

      const result = await Back.turnInReview(
        this.assignment,
        this.rating,
        this.amazonUsername,
        this.countryCode.country_code
      );

      // console.log("turn in result", result);

      this.$store.commit("hideTurnInReview");

      if (result) {
        if (this.assignment.is_flow_v2) {
          this.$toasted.show(
            "Great! Your review has been turned in! Thank you!",
            {
              icon: "check_circle",
              position: "bottom-center",
              duration: 3000
            }
          );
        } else {
          this.$toasted.show(
            "Great! Your review has been turned in and you've received your snaps!",
            {
              icon: "check_circle",
              position: "bottom-center",
              duration: 3000
            }
          );
        }

        Bus.$emit("UserChanged", this.book);
      } else {
        this.$toasted.show(
          "Oops! Something went wrong. Please try again later.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 3000
          }
        );
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-title {
  padding: 0.5em;
  padding-left: 30px;
  font-size: 21pt;
  font-weight: 500;
  color: #111;
  text-align: center;
}
.modal-instructions {
  font-size: 12pt;
  color: #111;
  text-align: center;
}
.modal-limit-instructions {
  padding: 60px 120px;
  font-size: 13.5pt;
  color: #111;
  text-align: center;
}
.modal-close {
  font-size: 15pt;
  margin-right: 6px;
  padding: 7px 0 0 6.5px;
  width: 36px;
  height: 36px;
  border-radius: 100px;
  float: right;
  position: relative;
  transition: all 0.2s ease-in-out;
}
.modal-close:hover {
  background: #eee;
  transform: scale(1.1);
  cursor: pointer;
}

.find-book {
  background: #fff;
  box-shadow: 0 0 1.5rem rgba(0, 0, 0, 0.14);
  font-family: $font-family;
}

.form-content {
  padding: 20px;
}

.input-label {
  float: left;
  width: 170px;
}
.input-box {
  float: left;
  width: 550px;
}

.input-label {
  padding: 20px 12px;
  font-size: 14pt;
  line-height: 1.4em;
}
.field-instructions {
  padding-bottom: 16px;
  font-size: 12.5pt;
  line-height: 1.4em;
  color: #777;
}
.field-error {
  padding-bottom: 16px;
  font-size: 12.5pt;
  line-height: 1.4em;
  color: rgb(235, 96, 96);
}

.actions {
  display: flex;
  justify-content: center;
  padding: 4px;
}
.action-selection {
  flex-grow: 0;
  text-align: center;
}
.action-refresh {
  flex-grow: 0;
  flex-basis: 260px;
  margin-left: auto;
  padding-right: 48px;
  text-align: center;
}
.action-refresh-item {
  float: right;
  width: 120px;
  text-align: center;
  border: 0px solid #f3f3f3;
  border-radius: 8px;
  margin: 0 8px;
  padding: 0.4em 0.75em;
  font-size: 12pt;
  color: #777;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
}
.action-refresh-item:hover {
  cursor: pointer;
  color: #999;
}
.action {
  background: #ede7f6;
  color: #9575cd;
}
.actions {
  text-align: center;
  padding-top: 20px;
}
.centered {
  text-align: center;
}

.confirm-text {
  padding: 0.75em 0 0 0;
  font-size: 11.5pt;
  color: #333;
  text-align: center;
}
.points-text {
  color: darken(#ffa000, 6%);
  font-weight: 700;
}
.book-title {
  color: $color-blue;
  font-weight: 700;
}

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0);
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
  z-index: 100000;
}

.modal-container {
  z-index: 999999;
  width: 800px;
  margin: 0px auto;
  padding: 20px 10px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0 2.5rem rgba(0, 0, 0, 0.19);
  transition: all 0.3s ease;
  font-family: "Source Sans Pro";
}

.modal-header h3 {
  margin-top: 0;
  color: #42b983;
}

.modal-body {
  margin: 20px 0;
}

.modal-default-button {
  float: right;
}

@media (max-width: 1024px) {
  // .modal-mask {
  //   position: absolute !important;
  // }
  .modal-container {
    height: auto;
    width: 100%;
    max-width: 100%;
    padding: 20px 0;
  }

  .modal-title {
    text-align: left;
    font-size: 19pt;
    padding-top: 1px;
    padding-left: 18px;
    padding-right: 0;
  }

  .modal-close {
    float: right !important;
    position: initial !important;
    margin-right: 12px !important;
  }

  .modal-limit-instructions {
    padding: 8px 16px 16px 16px;
  }
}

/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>


